import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	SetDigitalTwinsPageNumber,
	SetDigitalTwinsTotal,
	SetDigitalTwinsListOfBatteries,
} from "../../Actions/DigitalTwinsList";
import { getDigitalTwinConfigList, getDigitalTwinData } from "../../Api/DigitalTwinListApi";
import { SetSessionExpired } from "../../Actions";
import CODE from "../../Static/Constants/StatusCodes";
import { fetchFiltersDataObject } from "../../Helper/Table/TableHelper";
import AppCache from "../../Helper/Common/AppCache";
import GenericDraggableTableView from "../../Features/Table/GenericDraggableTable";
import { useTranslation } from 'react-i18next';
import { MAX_COLUMN_SELECTION_COUNT } from "../../Helper/DigitalTwin/DataFile";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import { getAvailableEmailDomains } from "../../Helper/Organization/EmailDomains";

const COLUMN_SETTING_STORAGE_KEY = "DIGITAL_TWIN_COLUMN_SETTINGS";
// Tab order: 0 All Vehicles, 1 Battery Configurations, 2 All Battery Packs,
// 3 Unmapped IOT Devices. Must stay in sync with the Tabs array in InventoryMain.jsx
// and the positional columnsConfig array in Inventory.jsx.
//
// This tab and All Vehicles both read filterConfigurations.digitalTwinListTable and tell
// themselves apart by these indices, so All Battery Packs was placed AFTER this tab —
// keeping 0 and 1 stable means no existing org's filter `tabs` arrays had to change.
const TAB_INDEX = 1;

export default function DigitalTwinConfigListTab({
	selectedFilters,
	setSelectedFilters,
	sorting,
	setSorting,
	visibleColumnKeys,
	setVisibleColumnKeys,
	pagesContent,
	permissions,
	navigate,
	columns: allColumns,
	totalConfigs,
	setOpenBatteryConfigDialog,
}) {
	const { t } = useTranslation();
	const { hasPermission } = usePermissions();
	const dispatch = useDispatch();
	const pageNumber = useSelector((state) => state.DigitalTwinsPageNumber.value);
	const rowsPerPage = useSelector((state) => state.DigitalTwinsRowsPerPage.value);
	const total = useSelector((state) => state.DigitalTwinsTotal.value);

	const [listOfBatteries, setListOfBatteries] = useState({
		batteries: [],
		responseStatus: { code: -1, msg: "" },
	});
	const [isLoading, setIsLoading] = useState(true);
	const [columns, setColumns] = useState([]);
	const [filters, setFilters] = useState({});
	const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
	const [selectedColumns, setSelectedColumns] = useState([]);
	const [isSelectAll, setIsSelectAll] = useState(false);
	const [columnOrder, setColumnOrder] = useState([]);
	const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);

	const lastFetchParams = useRef(null);
	const disableDownloads = pagesContent?.disableDownloads === true;
	const tabColumns = allColumns?.[TAB_INDEX]?.columns ?? [];

	const [list, setList] = useState({
		batteries: {},
		pages: { start: 1, end: 0, total: 0 },
		responseStatus: { code: CODE.LOADING, msg: "" },
	});

	const getCurrentSorting = () => {
		if (sorting && sorting[TAB_INDEX]) {
			return sorting[TAB_INDEX];
		}
		return {};
	};

	const setupTabColumns = useCallback(() => {
		if (columns.length > 0) return;

		const defaultColumns = (() => {
			const sorted = tabColumns.sort((a, b) => {
				const order = col => {
					if (col.fixed === "left") return 0;
					if (col.fixed === "right") return 2;
					return 1;
				};
				return order(a) - order(b);
			});

			const sliced = sorted.slice(0, MAX_COLUMN_SELECTION_COUNT);
			const leftAndNone = sliced.filter(c => c.fixed !== "right");
			const rightOnly = sliced.filter(c => c.fixed === "right");

			return [...leftAndNone, ...rightOnly];
		})();

		const columnSettingKeys = AppCache.getItem(COLUMN_SETTING_STORAGE_KEY) || {};
		const columnSettingTabKeys = columnSettingKeys[TAB_INDEX] || [];

		const filteredColumns = columnSettingTabKeys.length
			? tabColumns.filter(({ dataIndex }) => columnSettingTabKeys.includes(dataIndex))
			: defaultColumns;

		const filteredColumnKeys = columnSettingTabKeys.length
			? columnSettingTabKeys
			: defaultColumns.map(({ dataIndex }) => dataIndex);

		setDefaultSelectedColumns(defaultColumns.map(({ dataIndex }) => dataIndex));
		setColumns(filteredColumns);
		setSelectedColumns(filteredColumnKeys);
		setVisibleColumnKeys((prev) => ({
			...prev,
			[TAB_INDEX]: filteredColumnKeys
		}));
	}, [columns, tabColumns, setVisibleColumnKeys]);

	const setFiltersAndData = async () => {
		setList(listOfBatteries);
		setIsLoading(false);

		const digitalTwinTableFilters = pagesContent?.filterConfigurations?.digitalTwinListTable?.filters;
		if (!digitalTwinTableFilters) {
			return false;
		}

		const tabFilters = digitalTwinTableFilters.filter((filter) => {
			if (!filter.tabs) {
				return true;
			}
			return filter.tabs.includes(TAB_INDEX);
		});

		const filtersObj = await fetchFiltersDataObject(tabFilters, filters);

		const sortedFiltersObj = Object.fromEntries(
			Object.entries(filtersObj).sort(([, a], [, b]) => a.label.localeCompare(b.label))
		);

		setFilters(sortedFiltersObj);
	};

	const applySort = (key, order) => {
		const tempSort = [];
		tempSort[TAB_INDEX] = { sortBy: key, orderBy: order };
		setSorting(tempSort);
	};

	const onPageChange = (page, pageSize) => {
		dispatch(SetDigitalTwinsPageNumber(page));
	};

	async function getDigitalTwinConfigListData(params = {}) {
		const sortBy = sorting[TAB_INDEX]?.sortBy;
		const orderBy = sorting[TAB_INDEX]?.orderBy;
		let response = await getDigitalTwinConfigList({
			filters: selectedFilters,
			pageNumber,
			rowsPerPage,
			sortBy,
			orderBy,
			...params
		});
		return {
			responseCode: response.responseStatus.code,
			total: response.response.total?.total,
			data: response?.response?.data || [],
			responseStatus: response.responseStatus
		};
	}

	const setTotalRowKeysForSelectAll = async () => {
		let ids = [];
		const response = await getDigitalTwinConfigListData({ allIdsOnly: true });

		if (response && response.responseStatus.code === 200) {
			ids = response.data;
		}

		if (ids.length > 0) {
			setTotalSelectedRowKeys(new Set(ids));
		}
	};

	const onSelectChange = (record, selected, selectedKeys) => {
		const updatedSelectedKeys = new Set(totalSelectedRowKeys);

		if (selected) {
			updatedSelectedKeys.add(record.id);
		} else {
			updatedSelectedKeys.delete(record.id);
			if (isSelectAll) {
				setIsSelectAll(false);
			}
		}

		setTotalSelectedRowKeys(updatedSelectedKeys);
	};

	const onSelectAllChange = (selected, selectedRows, changeRows) => {
		setIsSelectAll(selected);
		if (selected) {
			setTotalSelectedRowKeys(new Set(selectedRows.map((row) => row?.id)));
			setTotalRowKeysForSelectAll();
			return;
		}

		setTotalSelectedRowKeys(new Set());
		return;
	};

	const getTotalRowSelection = () => {
		return Array.from(totalSelectedRowKeys);
	};

	const getTotalRowSelectionCount = () => {
		if (isSelectAll) {
			return list?.pages?.total;
		}
		return totalSelectedRowKeys.size;
	};

	const getCurrentPageSelection = () => {
		return Array.from(totalSelectedRowKeys);
	};

	const getCSVData = async () => {
		const res = await getDigitalTwinData({
			resourceIDs: getTotalRowSelection(),
			actionType: "download-csv",
			filters: selectedFilters,
			resourceType: "battery-config-list",
			columnOrder: columnOrder,
			columns: JSON.stringify(columns)
		});

		if (res && res.responseStatus.code === 200) {
			return res.response.data;
		}

		if (res && res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
		return [];
	};

	const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
		const res = await getDigitalTwinData({
			resourceIDs: getTotalRowSelection(),
			filters: selectedFilters,
			actionType: "mail",
			resourceType: "battery-config-list",
			columnOrder: columnOrder,
			columns: JSON.stringify(columns),
			emailTo: sendTo,
			emailCc: sendCc,
			subject: subject,
			body: body
		});

		if (res && res.responseStatus.code === 200) {
			return res.response.data;
		}

		if (res && res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
		return [];
	};

	const applySelectedColumns = (newSelectedColumns) => {
		const filteredColumns = tabColumns.filter((columnConfig) => {
			return newSelectedColumns.includes(columnConfig.dataIndex);
		});

		setColumns(filteredColumns);
		setSelectedColumns(newSelectedColumns);

		const updatedSelectedColumns = { ...AppCache.getItem(COLUMN_SETTING_STORAGE_KEY) || {} };
		updatedSelectedColumns[TAB_INDEX] = newSelectedColumns;
		AppCache.setItem(COLUMN_SETTING_STORAGE_KEY, updatedSelectedColumns);

		setVisibleColumnKeys((prev) => ({
			...prev,
			[TAB_INDEX]: newSelectedColumns
		}));

		setColumnOrder(filteredColumns.map((col) => col.headerLabel || col.title));
	};

	const handlePrimaryActionButtonClick = () => {
		return setOpenBatteryConfigDialog(true);
	};

	const shouldShowAddResourcesButton = () => {
		const rbacVersion = AppCache.getItem('rbacVersion') || 'v1';
		const rbacEnabled = AppCache.getItem('rbacEnabled') ?? false;

		// V2: Use RBAC permissions
		if (rbacVersion === 'v2' && rbacEnabled) {
			return hasPermission(FEATURE_CODES.DIGITAL_TWIN_CONFIG, 'create');
		}

		// V1: Use old permission string
		return permissions?.includes("add-config");
	};

	const onColumnOrderChange = (tableColumns) => {
		setColumnOrder(tableColumns.map((value) => value.headerLabel));
	};

	const shouldShowFilteredCount = () => {
		if (isLoading) {
			return false;
		}
		return Object.keys(selectedFilters || {}).length > 0;
	};

	const handleClearSelection = () => {
		setTotalSelectedRowKeys(new Set());
		setIsSelectAll(false);
	};

	async function GetListOfBatteries() {
		if (listOfBatteries.responseStatus.code !== -1) {
			setListOfBatteries({
				batteries: [],
				responseStatus: { code: -1, msg: "" },
			});
		}

		setIsLoading(true);
		const sortBy = sorting[TAB_INDEX]?.sortBy;
		const orderBy = sorting[TAB_INDEX]?.orderBy;

		await getDigitalTwinConfigList({
			filters: selectedFilters,
			pageNumber,
			rowsPerPage,
			sortBy,
			orderBy,
		}).then((res) => {
			if (res.responseStatus.code === 200) {
				setListOfBatteries({
					batteries: res.response.data,
					pages: res.response.total,
					responseStatus: res.responseStatus,
				});
				dispatch(SetDigitalTwinsListOfBatteries(res.response.data));
				dispatch(SetDigitalTwinsTotal(res.response.total?.total));
			} else {
				if (res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
				setListOfBatteries({
					batteries: [],
					pages: res.response.total,
					responseStatus: {
						code: res.responseStatus?.code,
						msg: res.responseStatus?.msg,
					},
				});
			}
			setIsLoading(false);
		});
	}

	useEffect(() => {
		setupTabColumns();
	}, [tabColumns, setupTabColumns]);

	useEffect(() => {
		if (!visibleColumnKeys[TAB_INDEX]?.length) {
			return;
		}

		const currentParams = {
			pageNumber,
			selectedFilters: JSON.stringify(selectedFilters),
			sorting: JSON.stringify(sorting[TAB_INDEX]),
			visibleColumnKeys: JSON.stringify(visibleColumnKeys[TAB_INDEX])
		};

		const paramsChanged = JSON.stringify(currentParams) !== JSON.stringify(lastFetchParams.current);

		if (paramsChanged) {
			lastFetchParams.current = currentParams;
			GetListOfBatteries();
		}
	}, [pageNumber, selectedFilters, sorting, visibleColumnKeys]);

	useEffect(() => {
		if (pageNumber !== 1) {
			dispatch(SetDigitalTwinsPageNumber(1));
		}
	}, [selectedFilters, sorting[TAB_INDEX]]);

	useEffect(() => {
		if (listOfBatteries.responseStatus.code !== CODE.LOADING) {
			setFiltersAndData();
		}
	}, [listOfBatteries]);

	const tableData = list.responseStatus.code === CODE.SUCCESS ? list.batteries : [];

	return (
		<GenericDraggableTableView
			tableID='BatteryConfigList'
			title={t('inventory:tabs.battery_config.label')}
			count={totalConfigs}
			primaryButtonLabel={t('inventory:tabs.battery_config.add_resource')}
			showPrimaryButton={shouldShowAddResourcesButton()}
			onPrimaryButtonClick={() => handlePrimaryActionButtonClick()}
			applySearch={() => {}}
			searchableFields={undefined}
			disableSearch={true}
			disableDownloads={disableDownloads}
			getTotalRowSelection={getTotalRowSelection}
			getTotalRowSelectionCount={getTotalRowSelectionCount}
			getCSVData={getCSVData}
			onSendEmail={onSendEmail}
			availableEmailDomains={getAvailableEmailDomains(pagesContent)}
			filters={filters}
			selectedFilters={selectedFilters}
			applyFilters={(args) => setSelectedFilters(args)}
			initialColumns={tabColumns}
			selectedColumns={selectedColumns}
			applySelectedColumns={applySelectedColumns}
			filteredCount={shouldShowFilteredCount() ? list?.pages?.total : 0}
			tableColumns={columns}
			tableData={tableData}
			onColumnOrderChange={onColumnOrderChange}
			columnOrder={columnOrder}
			isLoading={isLoading}
			pageNumber={pageNumber}
			rowsPerPage={rowsPerPage}
			total={total}
			applySort={applySort}
			onPageChange={onPageChange}
			onClearSelection={() => handleClearSelection()}
			emptyMessage="No matches found for filters applied"
			customRightActions={false}
			initialSorting={getCurrentSorting()}
			maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
			defaultSelectedColumns={defaultSelectedColumns}
			rowSelection={{
				selectedRowKeys: getCurrentPageSelection(),
				onSelect: onSelectChange,
				onSelectAll: onSelectAllChange
			}}
		/>
	);
}
